<template>
  <div class="max-w-full h-full flex-1 relative z-0 flex overflow-hidden bg-light-screen-background dark:bg-dark-screen-background">
    <div class="max-w-full w-full h-full overflow-hidden mx-auto flex flex-row">
      <div class="w-full h-full mx-auto overflow-hidden">
        <div v-show="false">
          <div class="pb-6 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-full">
            <div class="flex items-center">
              <h2 id="timeline-title" class="ml-1 mt-10 text-light-black-text dark:text-dark-title-text font-sans font-medium text-lg">
                {{ $t("Scores List") }}
              </h2>
            </div>
            <div
              class="flex p-0 flex-col-reverse justify-stretch space-y-6 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-2 md:mt-0 md:flex-row md:space-x-2">
              <button
                type="button"
                class="inline-flex items-center px-2.5 py-2 shadow-sm text-sm font-normal rounded-md text-white bg-light-brand-blue dark:bg-dark-brand-blue hover:bg-blue-dark focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue"
                @click="
                  () => {
                    show_create_new_score = true
                  }
                ">
                <Plus class="mr-[11px]" aria-hidden="true" />
                {{ $t("Create new score") }}
              </button>
            </div>
          </div>
        </div>
        <div v-if="!show_create_new_score" ref="metric_body">
          <div v-if="filtered_scores.length > 0" class="flex flex-wrap items-start min-w-full">
            <div
              v-for="metric in filtered_scores"
              :key="metric.id"
              tabindex="1"
              class="has-tooltip relative flex-auto w-[250px] max-w-[250px] rounded-xl bg-white px-3 py-3 shadow dark:bg-dark-screen-background border dark:border-dark-divider-border mx-1 mb-3 flex items-center space-x-3 focus-within:ring-2 focus:ring-2 focus-within:ring-blue-light focus:ring-blue-light active:ring-blue-light"
              @keyup.enter="editScore(metric)">
              <div class="flex-1 min-w-0">
                <div class="flex w-full items-center">
                  <div class="mr-1 -ml-1">
                    <NumbersIcon v-if="metric.input_type == 'Number'" class="w-[48px] h-[48px]" />
                    <TextIcon v-else-if="metric.input_type == 'Text'" class="w-[48px] h-[48px]" />
                    <TextareaIcon v-else-if="metric.input_type == 'Textarea'" class="w-[48px] h-[48px]" />
                    <TextareaIcon v-else-if="metric.input_type == 'RichTextarea'" class="w-[48px] h-[48px]" />
                    <SelectListIcon v-else-if="metric.input_type == 'Select'" class="w-[48px] h-[48px]" />
                    <DateIcon v-else-if="metric.input_type == 'Date'" class="w-[48px] h-[48px]" />
                    <DateTimeIcon v-else-if="metric.input_type == 'DateTime'" class="w-[48px] h-[48px]" />
                    <TimeIcon v-else-if="metric.input_type == 'Time'" class="w-[48px] h-[48px]" />
                    <CheckboxIcon v-else-if="metric.input_type == 'Checkbox'" class="w-[48px] h-[48px]" />
                    <UrlIcon v-else-if="metric.input_type == 'URL'" class="w-[48px] h-[48px]" />
                    <ChecklistIcon v-else-if="metric.input_type == 'Checklist'" class="w-[48px] h-[48px]" />
                    <OrderedListIcon v-else-if="metric.input_type == 'OrderedList'" class="w-[48px] h-[48px]" />
                    <BulletPointsListIcon v-else-if="metric.input_type == 'BulletPointsList'" class="w-[48px] h-[48px]" />
                    <ObjectReferenceIcon v-else-if="metric.input_type == 'objectReference'" class="w-[48px] h-[48px]" />
                    <DecimalIcon v-else-if="metric.input_type == 'Decimal'" class="w-[48px] h-[48px]" />
                    <PercentIcon v-else-if="metric.input_type == 'Percent'" class="w-[48px] h-[48px]" />
                    <CurrencyIcon v-else-if="metric.input_type == 'Currency'" class="w-[48px] h-[48px]" />
                    <StarRatingIcon v-else-if="metric.input_type == 'StarRating'" class="w-[48px] h-[48px]" />
                    <BarRatingIcon v-else-if="metric.input_type == 'BarRating'" class="w-[48px] h-[48px]" />
                  </div>
                  <div class="w-full flex justify-between">
                    <div class="focus:outline-none">
                      <p class="flex flex-col leading-[14px] text-[12px] font-medium text-common-grey">
                        <span class="h-3">
                          {{ metric.input_type }}
                        </span>
                        <span
                          class="pt-1.5 text-[15px] font-normal text-light-black-text dark:text-dark-title-text overflow-hidden overflow-ellipsis whitespace-nowrap max-w-min">
                          {{ metric.name }}
                        </span>
                      </p>
                    </div>
                    <div class="flex items-center">
                      <KeyRound v-if="metric.metric_colors.is_primary_metric_color" color="#0002E1" class="w-4 h-4" aria-hidden="true" />
                      <Palette v-if="metric.metric_colors.ranges" color="#2AC986" class="w-4 h-4 ml-1" aria-hidden="true" />
                    </div>
                  </div>
                </div>
                <hr class="w-full mt-2 border-light-divider-blue dark:border-dark-divider-border">
                <div class="flex w-full items-center cursor-pointer mt-2">
                  <div class="w-1/3 flex items-center justify-center" @click="editScore(metric)">
                    <Pencil class="w-5 h-5 text-gray-400 hover:text-[#27C884]" aria-hidden="true" />
                  </div>
                  <div class="w-1/3 flex items-center justify-center">
                    <Copy class="w-5 h-5 text-gray-400 hover:text-[#2DA7D7]" aria-hidden="true" />
                  </div>
                  <div class="w-1/3 flex items-center justify-center" @click="requestDeleteScore(metric)">
                    <Trash2 class="w-5 h-5 text-gray-400 hover:text-red-500" aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="bg-white dark:bg-dark-box-background rounded-lg shadow-sm p-8 text-center">
            <div class="flex flex-col items-center">
              <Info class="w-16 h-16 text-gray-400 dark:text-gray-500 mb-4" />
              <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {{ $t("No Scores found") }}!
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {{ $t("Get started by creating a new score") }}
              </p>
              <button
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                @click="show_create_new_score = true">
                <Plus class="w-5 h-5 mr-2" />
                <span>{{ $t("Create New Score") }}</span>
              </button>
            </div>
          </div>
        </div>
        <div v-else>
          <NewScore
            @close="show_create_new_score = false"
            @create="
              () => {
                show_create_new_score = false
              }
            " />
        </div>
      </div>
    </div>

    <TransitionRoot as="template" :show="show_delete_confirmation">
      <Dialog
        as="div"
        static
        class="fixed z-10 inset-0 overflow-y-auto"
        :open="show_delete_confirmation"
        @close="show_delete_confirmation = false">
        <div style="background: transparent" class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in duration-200"
            leave-from="opacity-100"
            leave-to="opacity-0">
            <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <TransitionChild
            as="template"
            class="bg-transparent"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <div
              class="inline-block bg-white align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div class="px-4 pt-5 pb-4 sm:p-6 sm:pb-4 bg-white dark:bg-dark-screen-background">
                <div class="sm:flex sm:items-start">
                  <div
                    class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TriangleAlert class="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900 dark:text-dark-body-text">
                      {{ $t("Deleting") }} '{{ score_to_be_deleted.name }}'
                    </DialogTitle>
                    <div class="mt-2">
                      <p class="text-sm text-gray-500 dark:text-dark-body-text">
                        {{ $t("Do you want to delete this score?") }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-50 dark:bg-dark-screen-background px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  ref="cancelButtonRef"
                  type="button"
                  class="mt-3 w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 bg-red-400 text-base font-medium text-white hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  @click="
                    () => {
                      deleteScore(score_to_be_deleted)
                      show_delete_confirmation = false
                    }
                  ">
                  {{ $t("Yes") }}
                </button>
                <button
                  ref="cancelButtonRef"
                  type="button"
                  class="mt-3 w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 bg-white dark:bg-dark-box-background text-base font-medium text-gray-700 dark:text-dark-body-text hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  @click="show_delete_confirmation = false">
                  {{ $t("Cancel") }}
                </button>
              </div>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script>
import NewScore from "@/views/dashboard/admin/NewScore.vue"
import NumbersIcon from "@/components/shared/icons/NumbersIcon.vue"
import DecimalIcon from "@/components/shared/icons/DecimalIcon.vue"
import TextIcon from "@/components/shared/icons/TextIcon.vue"
import TextareaIcon from "@/components/shared/icons/TextareaIcon.vue"
import SelectListIcon from "@/components/shared/icons/SelectListIcon.vue"
import DateIcon from "@/components/shared/icons/DateIcon.vue"
import DateTimeIcon from "@/components/shared/icons/DateTimeIcon.vue"
import TimeIcon from "@/components/shared/icons/TimeIcon.vue"
import CheckboxIcon from "@/components/shared/icons/CheckboxIcon.vue"
import UrlIcon from "@/components/shared/icons/UrlIcon.vue"
import ChecklistIcon from "@/components/shared/icons/ChecklistIcon.vue"
import OrderedListIcon from "@/components/shared/icons/OrderedListIcon.vue"
import BulletPointsListIcon from "@/components/shared/icons/BulletPointsListIcon.vue"
import ObjectReferenceIcon from "@/components/shared/icons/ObjectReferenceIcon.vue"
import PercentIcon from "@/components/shared/icons/PercentIcon.vue"
import CurrencyIcon from "@/components/shared/icons/CurrencyIcon.vue"
import StarRatingIcon from "@/components/shared/icons/StarRatingIcon.vue"
import BarRatingIcon from "@/components/shared/icons/BarRatingIcon.vue"
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue"
import { Palette, Plus, Trash2, Pencil, Copy, Info, TriangleAlert, KeyRound } from "lucide-vue-next"

export default {
  components: {
    NewScore,
    Info,
    NumbersIcon,
    DecimalIcon,
    TextIcon,
    TextareaIcon,
    SelectListIcon,
    DateIcon,
    DateTimeIcon,
    TimeIcon,
    CheckboxIcon,
    UrlIcon,
    ChecklistIcon,
    OrderedListIcon,
    BulletPointsListIcon,
    ObjectReferenceIcon,
    PercentIcon,
    CurrencyIcon,
    StarRatingIcon,
    BarRatingIcon,
    KeyRound,
    Palette,
    Plus,
    Copy,
    TriangleAlert,
    Pencil,
    Trash2,
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot
  },
  props: {
    showAddNewScore: {
      type: Boolean,
      required: false
    },
    scoreFilter: {
      type: String,
      required: true
    },
    objectType: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      score_to_edit: null,
      show_create_new_score: false,
      show_delete_confirmation: false,
      score_to_be_deleted: null
    }
  },
  computed: {
    filtered_scores() {
      return this.$store.state.metrics
        .filter((m) => m.metric_class == "score_metric")
        .filter(
          (m) =>
            (!this.scoreFilter ||
              (this.scoreFilter &&
                m.name.toLowerCase().search(this.scoreFilter.toLowerCase().replaceAll("(", "").replaceAll(")", "")) != -1)) &&
            (!this.objectType || (this.objectType && m.object_type == this.objectType))
        )
    }
  },
  watch: {
    showAddNewScore: {
      handler() {
        this.show_create_new_score = this.showAddNewScore
      }
    }
  },
  mounted() {
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["Scores"])
    this.show_create_new_score = this.showAddNewScore
  },
  activated() {
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", ["Scores"])
  },
  methods: {
    editScore(score) {
      this.score_to_edit = score
    },

    requestDeleteScore(score) {
      this.score_to_be_deleted = score
      this.show_delete_confirmation = true
    },

    deleteScore(score) {
      this.$store.dispatch("objects/deleteMetric", score)
    }
  }
}
</script>
